import { Button, Field, Icons, TextArea, TextButton } from "../../../ds";
import { ChangesetConflictDTO, ChangesetMergeDTO, ResolutionChoices } from "../models/ChangeRequestDTO";
import { ConflictPiece, Resolution, Resolutions, conflictKey, resolvedText, splitMerged } from "../utils/conflicts";
import { Fragment, useMemo, useState } from "react";

import { Marked } from "./RebaseConflicts";
import { SideLabels } from "../utils/labels";

/** Lines kept on each side of a conflict when the text between two of them folds. */
const Context = 3;
/** A run of unchanged lines shorter than this is shown whole. */
const FoldAbove = Context * 2 + 2;

interface IMergedContentProps {
    merge: ChangesetMergeDTO;
    /** The line conflicts of this op (`isLineConflict`). */
    conflicts: ChangesetConflictDTO[];
    resolutions: Resolutions;
    /** The author of an open change request resolves; everyone else reads. */
    canResolve: boolean;
    onResolve: (key: string, resolution: Resolution | undefined) => void;
    /** The words for each side, by who reads. */
    labels: SideLabels;
}

/** One merged line, in the design system's diff rows (one line-number column: the merged text has one numbering). */
function Line(props: { no?: number; text: string; css?: string; base?: string }) {
    return (
        <tr className={`ark-diff-row is-same cr-merge-row${props.css ? ` ${props.css}` : ""}`}>
            <td className="ark-diff-no" aria-hidden>{props.no ?? ""}</td>
            <td className="ark-diff-text">{props.base !== undefined ? <Marked base={props.base} value={props.text} /> : props.text || " "}</td>
        </tr>
    );
}

/** Past this many lines in one conflict, the words are not marked: each line is paired with the base line at the same place only. */
const MarkedLines = 200;

/** The base line a side's line is compared with for its word marks: the one at the same place, when the run is small enough to read that way. */
function baseFor(base: string, lines: string[]): (i: number) => string | undefined {
    const baseLines = base === "" ? [] : base.split("\n");
    if (lines.length > MarkedLines || baseLines.length > MarkedLines) return () => undefined;
    return (i) => baseLines[i];
}

/** A side longer than this shows its first lines and the rest on request: a run that long is read in the page, not here. */
const SideShown = 100;

/** The lines of one side of a conflict, each word that differs from the base line in its place marked. */
function Side(props: { label: string; lines: string[]; base: string; css: string }) {
    const [all, setAll] = useState(false);
    const base = baseFor(props.base, props.lines);
    const shown = all || props.lines.length <= SideShown * 1.5 ? props.lines : props.lines.slice(0, SideShown);
    const rest = props.lines.length - shown.length;
    return (
        <div className={`cr-conflict-side ${props.css}`}>
            <div className="cr-conflict-side-label">{props.label}{props.lines.length > 1 ? ` · ${props.lines.length} lines` : ""}</div>
            <div className="cr-conflict-code">
                {props.lines.length === 0 ? (
                    <div className="cr-merge-line cr-conflict-empty">(no lines)</div>
                ) : (
                    shown.map((l, i) => {
                        const b = base(i);
                        return <div key={i} className="cr-merge-line">{b !== undefined ? <Marked base={b} value={l} /> : l || " "}</div>;
                    })
                )}
            </div>
            {rest > 0 ? (
                <TextButton type="white" className="cr-conflict-more" text={`⋯ Show the other ${rest} lines of this side`} onClick={() => setAll(true)} />
            ) : null}
        </div>
    );
}

function ConflictBlock(props: { piece: ConflictPiece; resolution?: Resolution; canResolve: boolean; labels: SideLabels; onResolve: (r: Resolution | undefined) => void }) {
    const { piece, resolution, labels } = props;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(piece.ours.join("\n"));
    const c = piece.conflict;
    const base = c.base ?? "";
    const where = c.line ? `line ${c.line}` : "here";
    const title = resolution ? `Resolved: ${labels.resolved[resolution.choice]}` : editing ? `Both changed ${where} · editing` : `Both changed ${where}`;

    let chosen: React.ReactNode = null;
    if (resolution) {
        const text = resolvedText(c, resolution);
        const chosenLines = text === "" ? [] : text.split("\n");
        const baseAt = baseFor(base, chosenLines);
        chosen = chosenLines.length === 0
            ? <Line text="(no lines)" css="cr-merge-resolved cr-conflict-empty" />
            : chosenLines.map((l, i) => <Line key={i} no={piece.from + i} text={l} css="cr-merge-resolved" base={baseAt(i)} />);
    }

    return (
        <tbody className="cr-merge-conflict" aria-label={`Conflict at ${where}`}>
            <tr className="cr-merge-conflict-row">
                <td colSpan={2}>
                    <div className={`cr-conflict-card${resolution ? " is-resolved" : ""}`}>
                        <div className="cr-conflict-head">
                            <span className="cr-conflict-icon">{resolution ? <Icons.check size={16} /> : <Icons.warning size={16} />}</span>
                            <span className="cr-conflict-title">{title}</span>
                            {resolution && props.canResolve ? (
                                <TextButton className="cr-conflict-head-aside" text="Change" onClick={() => { props.onResolve(undefined); setEditing(false); }} />
                            ) : !props.canResolve && labels.decides && !resolution ? (
                                <span className="cr-conflict-head-aside cr-conflict-decides">{labels.decides}</span>
                            ) : null}
                        </div>

                        {resolution ? null : editing ? (
                            <div className="cr-conflict-edit">
                                <Field label={`Your lines at ${where}`}>
                                    <TextArea value={draft} rows={Math.max(3, draft.split("\n").length + 1)} onChange={setDraft} />
                                </Field>
                                <div className="cr-conflict-choices">
                                    <Button type="secondary" size="sm" text="Use these lines" onClick={() => { props.onResolve({ choice: ResolutionChoices.Manual, value: draft }); setEditing(false); }} />
                                    <TextButton type="white" text="Cancel" onClick={() => setEditing(false)} />
                                </div>
                                <div className="cr-conflict-sides is-reference" aria-label="For reference">
                                    <Side label={labels.ours} lines={piece.ours} base={base} css="is-ours" />
                                    <Side label={labels.theirs} lines={piece.theirs} base={base} css="is-theirs" />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="cr-conflict-sides">
                                    <Side label={labels.ours} lines={piece.ours} base={base} css="is-ours" />
                                    <Side label={labels.theirs} lines={piece.theirs} base={base} css="is-theirs" />
                                </div>
                                {props.canResolve ? (
                                    <div className="cr-conflict-choices">
                                        <Button type="secondary" size="sm" text={labels.keepOurs} onClick={() => props.onResolve({ choice: ResolutionChoices.Ours })} />
                                        <Button type="secondary" size="sm" text={labels.takeTheirs} onClick={() => props.onResolve({ choice: ResolutionChoices.Theirs })} />
                                        <TextButton text="Edit" icon={<Icons.edit size={14} />} onClick={() => { setDraft(piece.ours.join("\n")); setEditing(true); }} />
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </td>
            </tr>
            {chosen}
        </tbody>
    );
}

/**
 * The content as an update would leave it: the page's edits since the proposal and the author's merged, line by line, with each
 * place both changed shown as a card with both versions side by side. The author picks a side (or writes the lines) for each; the
 * choices go with Update.
 */
export function MergedContent(props: IMergedContentProps) {
    const pieces = useMemo(() => splitMerged(props.merge, props.conflicts), [props.merge, props.conflicts]);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    if (props.merge.content == null) return <div className="cr-diff-empty">The content does not change.</div>;

    const fold = (i: number, hidden: number) => (
        <tbody>
            <tr className="ark-diff-fold">
                <td colSpan={2}>
                    <button type="button" onClick={() => setExpanded((prev) => new Set(prev).add(i))}>
                        ⋯ Show {hidden} unchanged line{hidden === 1 ? "" : "s"}
                    </button>
                </td>
            </tr>
        </tbody>
    );

    return (
        <div className="ark-diff cr-merged" role="group" aria-label="Merged content">
            <div className="ark-diff-scroll">
                <table className="ark-diff-table cr-merge-table">
                    {pieces.map((piece, i) => {
                        if (piece.kind === "conflict") {
                            const key = conflictKey(piece.conflict);
                            return (
                                <ConflictBlock key={key} piece={piece} resolution={props.resolutions[key]} canResolve={props.canResolve} labels={props.labels}
                                    onResolve={(r) => props.onResolve(key, r)} />
                            );
                        }
                        const folds = piece.lines.length > FoldAbove && !expanded.has(i);
                        if (!folds) {
                            return <tbody key={i}>{piece.lines.map((l, k) => <Line key={k} no={piece.from + k} text={l} />)}</tbody>;
                        }
                        // the first piece needs no lines before the fold, the last none after
                        const head = i === 0 ? 0 : Context;
                        const tail = i === pieces.length - 1 ? 0 : Context;
                        const hidden = piece.lines.length - head - tail;
                        return (
                            <Fragment key={i}>
                                <tbody>{piece.lines.slice(0, head).map((l, k) => <Line key={k} no={piece.from + k} text={l} />)}</tbody>
                                {fold(i, hidden)}
                                <tbody>
                                    {piece.lines.slice(piece.lines.length - tail).map((l, k) => (
                                        <Line key={k} no={piece.from + piece.lines.length - tail + k} text={l} />
                                    ))}
                                </tbody>
                            </Fragment>
                        );
                    })}
                </table>
            </div>
        </div>
    );
}
